<template>
  <div class="wrapper">
    <div class="main visual-2 avdoc">
      <Header />

      <!-- Loader -->
      <div v-if="loading" class="spinner">
        <div class="loader"></div>
        <p>Loading doctors...</p>
      </div>

      <!-- Doctors Section -->
      <div v-else>
        <div class="doc-search">
          <div class="">
            <!-- <label for="" class="mb-2">Search:</label> -->
            <input
              type="text"
              v-model="search"
              placeholder="Search..."
              class="form-control text-center"
            />
          </div>
        </div>

        <div class="vis4-inr1" v-if="paginatedDoctors.length">
          <h1>Available Doctors</h1>

          <div class="available-doctor-carousel">
            <div class="doctor-card-container">
              <div
                v-for="(doctor, index) in paginatedDoctors"
                :key="index"
                class="doctor-card-wrapper"
              >
                <div class="doctor-card">
                  <img
                    :src="doctorImage(doctor?.photo)"
                    alt="Doctor"
                    class="doctor-card-image"
                  />
                  <!-- <p class="doctor-card-info">
                    <span class="doctor-card-rating"
                      >⭐ {{ roundedAverageRating(doctor.doctor) }}</span
                    >
                    <span class="doctor-card-reviews">
                      | {{ formattedTotalReviews(doctor.doctor) }} Reviews
                    </span>
                  </p> -->
                  <div class="rating-exp">
                    <p class="">{{ doctor?.experienceYears }} Yrs experiece</p>
                  </div>
                  <div class="time-fee">
                    <p>
                      <i class="fa-solid fa-calendar-days"></i>
                      {{ doctor.workingDays }}<br />
                      <i class="fa-solid fa-clock"></i> {{ doctor?.sittingTime
                      }}<br />
                    </p>
                    <p>
                      <i class="fa-solid fa-money-bill"></i>
                      {{
                        $formatHospitalCurrency(
                          doctor?.doctor.consultationFee,
                          doctor?.hospital?.id || doctor?.doctor?.hospitalId
                        )
                      }}
                      <br />
                      <i class="fa-solid fa-graduation-cap"></i>
                      {{ limitedText(doctor?.doctor?.speciality, 16) }}
                    </p>
                  </div>

                  <h3 class="doctor-card-name">
                    {{ doctor.doctor.firstname }} {{ doctor.doctor.lastname }}
                  </h3>
                  <div class="doctor-card-time-slots">
                    <button
                      class="time-slot-button"
                      :disabled="loadingDoctorId === doctor.id"
                      @click="selectDoctor(doctor.id)"
                    >
                      Select
                      <span
                        v-if="loadingDoctorId === doctor.id"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="doctor-card-pagination" v-if="totalPages > 1">
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="pagination-button"
              >
                Prev
              </button>
              <button
                v-for="page in totalPages"
                :key="page"
                :class="[
                  'pagination-button',
                  { 'pagination-active': currentPage === page },
                ]"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        <div class="vis4-inr1" v-else>
          <h1>No doctors available!</h1>
        </div>
      </div>

      <!-- Help Button -->
      <FooterView :showButtons="true" />
    </div>
  </div>
</template>

<script>
import { DoctorTimeSlotService } from "@/services/doctor-time-slots.service";
import { PatientTimeSlotsService } from "@/services/patient-time-slots.service";
import FooterView from "@/layout/Footer.vue";
import Header from "@/layout/Header.vue";
import { lowercaseString } from "@/utils/helpers";
export default {
  components: {
    FooterView,
    Header,
  },
  name: "AvailableDoctor",
  data() {
    return {
      doctors: [], // List of doctors fetched from the API
      currentPage: 1,
      itemsPerPage: 4,
      loadingDoctorId: null, // Tracks the currently loading doctor
      loading: true, // Tracks the overall loading state
      search: "",
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredDoctors.length / this.itemsPerPage);
    },
    filteredDoctors() {
      return this.doctors.filter((doctor) => {
        const fullName =
          `${doctor.doctor.firstname} ${doctor.doctor.lastname}`.toLowerCase();
        return fullName.includes(this.search.toLowerCase());
      });
    },
    paginatedDoctors() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDoctors.slice(start, end);
    },
  },
  created() {
    let data = JSON.parse(localStorage.getItem("patient"));
    if (!data) {
      this.$router.push("/appointments");
      return;
    }
  },
  methods: {
    roundedAverageRating(data) {
      return data?.averageRating?.averageRating
        ? data.averageRating.averageRating.toFixed(1) // Rounds to 1 decimal place
        : "0.0";
    },
    formattedTotalReviews(data) {
      const reviews = data?.averageRating?.totalReviews || 0;
      if (reviews >= 1_000_000) {
        return (reviews / 1_000_000).toFixed(1) + "M";
      } else if (reviews >= 1_000) {
        return (reviews / 1_000).toFixed(1) + "K";
      }
      return reviews.toString();
    },
    formatTime(time) {
      if (!time) return "";

      // Convert UTC time string to a local Date object
      let currentDateLocal = new Date(time);

      // Format the Date object to a local time string
      return currentDateLocal.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Ensures 12-hour format with AM/PM
      });
    },

    async fetchDoctors() {
      this.loading = true; // Show the loader
      try {
        const response = await DoctorTimeSlotService.getList();
        if (response.success && response.data.length > 0) {
          this.doctors = response.data;
        } else {
          this.doctors = []; // No doctors available
        }
      } catch (error) {
        console.error("Error fetching doctor time slots:", error);
        this.doctors = [];
      } finally {
        this.loading = false; // Hide the loader
      }
    },
    async selectDoctor(doctorSlotId) {
      this.loadingDoctorId = doctorSlotId; // Set loading state for doctor selection
      try {
        const patient = JSON.parse(localStorage.getItem("patient"));

        const payload = {
          firstname: patient.firstname,
          lastname: patient.lastname,
          doctorSlotId: doctorSlotId,
          address: patient.address || "Not provided",
          communicationType: patient.communicationType,
          email: lowercaseString(patient.email) || null,
          phoneNumber: patient.phone || null,
        };

        const response = await PatientTimeSlotsService.create(payload);
        if (response.success) {
          localStorage.removeItem("patient");
          this.$router.push({
            path: "/appointment-status",
            query: { id: response.data.id },
          });
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: response.message,
          });
        }
      } catch (error) {
        console.error("Error creating patient time slot:", error);
      } finally {
        this.loadingDoctorId = null; // Reset loading state
      }
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    goToPage(page) {
      this.currentPage = page;
    },
    limitedText(text, charLimit = 20) {
      if (!text) return "";
      return text.length > charLimit ? text.slice(0, charLimit) + "..." : text;
    },
    doctorImage(photoUrl) {
      return photoUrl || require("@/assets/img/doctor-james.png");
    },
  },
  mounted() {
    this.fetchDoctors();
  },
  beforeRouteLeave(to, from, next) {
    if (to.path === "/appointments") {
      localStorage.removeItem("patient");
    }
    next();
  },
};
</script>
<style scoped>
.spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
}

.loader {
  border: 8px solid #f3f3f3;
  /* Light gray */
  border-top: 8px solid #3498db;
  /* Blue */
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.doctor-card-time-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.time-slot-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 53px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 19px;
}

.time-slot-button:hover {
  background-color: #0056b3;
}

.specialty {
  margin-bottom: 0;
  color: #007cf8;
  border-radius: 30px;
  padding: 0px 0px;
  text-align: right;
  font-size: 15px;
  font-weight: 700;
}

.rating-exp {
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
}

.time-fee p i {
  color: #007cf8;
}

.time-fee {
  display: flex;
  justify-content: space-between;
  padding: 10px 10px 0;
  text-align: left;
}

.rating-exp p {
  margin-bottom: 5px;
}

p.doctor-card-info {
  position: absolute;
  top: 5px;
  right: 5px;
  color: #fff;
  border-radius: 10px;
  padding: 2px 6px;
  text-shadow: 1px 1px 3px black;
}
</style>
